import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { IconCircleCheck, IconCircleX, IconDeviceFloppy, IconPlus } from "@tabler/icons-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Group,
  IconAction,
  Input,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui";
import { qk } from "@/lib/query-keys";
import { toastError, toastErrorApi, toastExito } from "@/lib/toast";
import { cecosQueryOptions, cuentasQueryOptions } from "@/features/catalogos/hooks/use-catalogos";
import {
  crearCentroDeCosto,
  crearCuentaContable,
} from "@/features/admin-catalogos-contables/services/catalogos-contables-admin-service";

export type EstadoCatalogo = "na" | "cargando" | "si" | "no";
export type TipoCatalogo = "cuenta" | "ceco";

/**
 * Verifica cuenta contable / centro de costos contra los catálogos. Compartido
 * por los previews de importación de OC y CA. Devuelve helpers `estadoCuenta`
 * y `estadoCeco` para saber si un código existe en su catálogo.
 */
export function useCatalogoVerificacion() {
  const cuentas = useQuery(cuentasQueryOptions);
  const cecos = useQuery(cecosQueryOptions);
  const cargando = cuentas.isLoading || cecos.isLoading;

  const cuentasSet = new Set((cuentas.data ?? []).map((c) => c.codigo.trim()).filter(Boolean));
  const cecosSet = new Set((cecos.data ?? []).map((c) => c.codigo.trim()).filter(Boolean));

  const estado = (valor: string | null, set: Set<string>): EstadoCatalogo => {
    if (!valor || !valor.trim()) return "na";
    if (cargando) return "cargando";
    return set.has(valor.trim()) ? "si" : "no";
  };

  return {
    cargando,
    estadoCuenta: (v: string | null) => estado(v, cuentasSet),
    estadoCeco: (v: string | null) => estado(v, cecosSet),
  };
}

/** Celda con indicador de existencia y, si falta, botón para dar de alta el código. */
export function CeldaCatalogo({
  valor,
  estado,
  onAgregar,
}: {
  valor: string | null;
  estado: EstadoCatalogo;
  onAgregar?: () => void;
}) {
  if (!valor) return <>—</>;
  return (
    <Group gap={4} className="flex-nowrap">
      <Text size="sm" c={estado === "no" ? "peligro" : undefined}>{valor}</Text>
      {estado === "si" && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex text-exito-600">
              <IconCircleCheck size={15} />
            </span>
          </TooltipTrigger>
          <TooltipContent>Existe en el catálogo</TooltipContent>
        </Tooltip>
      )}
      {estado === "no" && (
        <>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex text-peligro-600">
                <IconCircleX size={15} />
              </span>
            </TooltipTrigger>
            <TooltipContent>No existe en el catálogo</TooltipContent>
          </Tooltip>
          {onAgregar && (
            <IconAction label="Agregar al catálogo" size="xs" variant="light" iconColor="exito-600" onClick={onAgregar}>
              <IconPlus size={12} />
            </IconAction>
          )}
        </>
      )}
    </Group>
  );
}

/**
 * Alta rápida de una cuenta contable o centro de costos, prellenada con el
 * código que falta en el catálogo. Al guardar invalida el catálogo para que
 * los indicadores del preview pasen a verde.
 */
export function AltaCatalogoModal({
  tipo,
  codigoInicial,
  onClose,
}: {
  tipo: TipoCatalogo | null;
  codigoInicial: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [codigo, setCodigo] = useState(codigoInicial);
  const [nombre, setNombre] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    setCodigo(codigoInicial);
    setNombre("");
  }, [codigoInicial, tipo]);

  const esCuenta = tipo === "cuenta";
  const titulo = esCuenta ? "Agregar cuenta contable" : "Agregar centro de costos";

  const submit = async () => {
    if (!codigo.trim() || !nombre.trim()) {
      toastError({ title: "Datos incompletos", message: "Código y nombre son obligatorios." });
      return;
    }
    setGuardando(true);
    try {
      const input = { codigo: codigo.trim(), nombre: nombre.trim() };
      if (esCuenta) await crearCuentaContable(input);
      else await crearCentroDeCosto(input);
      toastExito({
        title: esCuenta ? "Cuenta contable creada" : "Centro de costos creado",
        message: `Código ${input.codigo} agregado al catálogo.`,
      });
      await qc.invalidateQueries({ queryKey: esCuenta ? qk.cuentas : qk.cecos });
      onClose();
    } catch (e) {
      toastErrorApi(e);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={tipo !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
        </DialogHeader>
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            El código viene prellenado del documento. Completa el nombre y guárdalo para darlo de alta
            en el catálogo.
          </Text>
          <Input label="Código" required className="font-mono" value={codigo} onChange={(e) => setCodigo(e.currentTarget.value)} />
          <Input label="Nombre" required placeholder={esCuenta ? "Ej. Arrendamientos" : "Ej. Banca Privada"} value={nombre} onChange={(e) => setNombre(e.currentTarget.value)} autoFocus />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Cancelar</Button>
            <Button leftSection={<IconDeviceFloppy size={14} />} loading={guardando} onClick={submit}>
              Guardar
            </Button>
          </Group>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
